import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { SessionInfo } from '../../shared/session';
import { ApiError, api } from '../lib/api';

const safeNext = (n: string | null) => (n?.startsWith('/') && !n.startsWith('//') ? n : '/');

export function LoginPage({ session }: { session: SessionInfo | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dev = session?.authMode === 'dev';

  if (session?.authenticated) {
    queueMicrotask(() => navigate(safeNext(params.get('next')), { replace: true }));
  }

  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['session'] });
    navigate(safeNext(params.get('next')), { replace: true });
  };
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.status === 403
            ? 'This account is not a console administrator.'
            : e.status === 401
              ? 'Invalid or expired code.'
              : e.status === 429
                ? 'Too many attempts. Try again later.'
                : e.message
          : 'Sign-in failed.',
      );
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (dev)
      return void run(async () => {
        await api.devLogin(email);
        await done();
      });
    if (step === 'email')
      return void run(async () => {
        await api.sendCode(email);
        setStep('code');
      });
    return void run(async () => {
      await api.verify(email, code);
      await done();
    });
  };

  return (
    <div className="login">
      <div className="card panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="brandmark" style={{ padding: 0 }}>
            <div className="mark" aria-hidden="true">
              BI
            </div>
            <div>
              <div className="t1">Brand Intelligence</div>
              <div className="t2">Console</div>
            </div>
          </div>
        </div>
        <h1>{dev ? 'Local development sign-in' : 'Sign in to the Brand Console'}</h1>
        <p className="muted" style={{ margin: 0 }}>
          Administrator access only. Use your rsecure.ai work email.
        </p>
        {session && (
          <p style={{ margin: '10px 0 0' }}>
            <span className="env-badge">{session.environment}</span>
          </p>
        )}
        <form onSubmit={submit} noValidate>
          <label htmlFor="email" className="field-label">
            Work email
          </label>
          <input
            id="email"
            className="input ltr"
            type="email"
            autoComplete="email"
            placeholder="Work email"
            value={email}
            disabled={step === 'code'}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {step === 'code' && (
            <>
              <p className="muted" style={{ margin: 0, fontSize: 13 }} role="status">
                If this address is an administrator, a code is on its way.
              </p>
              <label htmlFor="code" className="field-label">
                6-digit code
              </label>
              <input
                id="code"
                className="input ltr mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </>
          )}
          {error && (
            <div className="err" role="alert">
              {error}
            </div>
          )}
          <button
            className="btn primary"
            type="submit"
            disabled={busy || !email || (step === 'code' && code.length !== 6)}
          >
            {busy ? <span className="spinner" aria-hidden="true" /> : null}
            {dev
              ? 'Local development sign-in'
              : step === 'email'
                ? 'Email me a sign-in code'
                : 'Verify and sign in'}
          </button>
          {step === 'code' && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setStep('email');
                setCode('');
              }}
            >
              Use a different email
            </button>
          )}
          {dev && (
            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
              Development mode: no email is sent. Only allowlisted administrator emails are
              accepted. Disabled in production.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
